/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getFirms, createFirm, updateFirm, deleteFirm } from '../utils/api';
import FirmContactCard from './FirmContactCard';
import KpiCards from './KpiCards';
import useBreakpoint, { canHover } from '../hooks/useBreakpoint';
import useAutoRefresh, { isSameData } from '../hooks/useAutoRefresh';

export const STATUS_OPTIONS = [
  'Lead',
  'Attempted Contact',
  'Contacted',
  'Follow-Up',
  'Interested',
  'Demo Scheduled',
  'Design Partner Candidate',
  'Not Interested',
  'Do Not Contact',
  'Converted → Design Partner',
];

const STATUS_COLORS = {
  'Lead':                        '#7A8FAA',
  'Attempted Contact':           '#2EA2EB',
  'Contacted':                   '#2EA2EB',
  'Follow-Up':                   '#FBBF24',
  'Interested':                  '#34D399',
  'Demo Scheduled':              '#A78BFA',
  'Design Partner Candidate':    '#FB923C',
  'Not Interested':              '#9CA3AF',
  'Do Not Contact':              '#F87171',
  'Converted → Design Partner':  '#10B981',
};

// tel: link from a display phone number; anything after an extension marker ("x123", "ext 5") is dropped.
const telHref = (phone) => `tel:${String(phone).split(/ext|x/i)[0].replace(/[^\d+]/g, '')}`;

function StatusBadge({ status }) {
  const color = STATUS_COLORS[status] || '#7A8FAA';
  return (
    <span style={{
      background: `${color}1a`, color, borderRadius: 20,
      padding: '2px 8px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
    }}>
      {status}
    </span>
  );
}

export default function FirmPipeline({ showToast, reloadKey, refreshKey, onRefreshed, onOpenDesignPartner, openFirmId, onOpenFirmHandled }) {
  const [firms,        setFirms]        = useState([]);
  const [loading,       setLoading]     = useState(true);
  const [detailId,      setDetailId]    = useState(null);
  const [editingCell,   setEditingCell] = useState(null); // { id, field }
  const [draftValue,    setDraftValue]  = useState('');
  const [search,        setSearch]      = useState('');
  const [statusFilter,  setStatusFilter]= useState('');
  const [newFirmName,   setNewFirmName] = useState('');
  const [adding,        setAdding]      = useState(false);
  const [selected,      setSelected]    = useState(new Set());
  const [bulkDeleting,  setBulkDeleting] = useState(false);
  const scrollRef = useRef();
  const isPhone = useBreakpoint() === 'phone';

  const toast = showToast || (() => {});

  const loadFirms = useCallback(async () => {
    try {
      setFirms(await getFirms());
    } catch {
      toast('Failed to load firms', 'error');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { loadFirms(); }, [loadFirms, reloadKey]);

  // Background refresh: no spinner, no toast, state only changes when the data did. Selection
  // is pruned to ids that still exist by the visible-rows effect below.
  const silentRefresh = useCallback(async (shouldApply) => {
    const data = await getFirms();
    if (!shouldApply()) return;
    setFirms(prev => (isSameData(prev, data) ? prev : data));
  }, []);

  // Paused while a contact card is open, a cell is being edited, or an add / bulk delete is in flight.
  const { refreshNow } = useAutoRefresh(silentRefresh, {
    paused: detailId !== null || editingCell !== null || adding || bulkDeleting,
    onRefreshed,
  });

  // Header Refresh button.
  const lastRefreshKey = useRef(refreshKey);
  useEffect(() => {
    if (lastRefreshKey.current === refreshKey) return;
    lastRefreshKey.current = refreshKey;
    refreshNow().catch(() => toast('Failed to load firms', 'error'));
  }, [refreshKey, refreshNow, toast]);

  useEffect(() => {
    if (openFirmId) {
      setDetailId(openFirmId);
      onOpenFirmHandled?.();
    }
  }, [openFirmId]);

  const handleQuickAdd = async (e) => {
    e.preventDefault();
    if (!newFirmName.trim()) return;
    setAdding(true);
    try {
      const firm = await createFirm({ firm_name: newFirmName.trim() });
      setNewFirmName('');
      await loadFirms();
      setDetailId(firm.id);
    } catch {
      toast('Failed to add firm', 'error');
    } finally {
      setAdding(false);
    }
  };

  const saveField = async (id, field, value) => {
    const firm = firms.find(f => f.id === id);
    if (!firm) return;
    try {
      const updated = await updateFirm(id, { ...firm, [field]: value });
      setFirms(prev => prev.map(f => f.id === id ? { ...f, ...updated } : f));
    } catch {
      toast('Save failed', 'error');
    }
    setEditingCell(null);
  };

  const startEdit = (id, field, currentValue) => {
    setEditingCell({ id, field });
    setDraftValue(currentValue ?? '');
  };

  const commitEdit = (id, field) => {
    saveField(id, field, draftValue === '' ? null : draftValue);
  };

  const scrollH = (dir) => scrollRef.current?.scrollBy({ left: dir * 200, behavior: 'smooth' });
  const scrollV = (dir) => scrollRef.current?.scrollBy({ top: dir * 150, behavior: 'smooth' });

  let filtered = firms;
  if (statusFilter) filtered = filtered.filter(f => f.status === statusFilter);
  if (search) {
    const q = search.toLowerCase();
    filtered = filtered.filter(f =>
      f.firm_name?.toLowerCase().includes(q) ||
      f.contact_name?.toLowerCase().includes(q) ||
      f.email?.toLowerCase().includes(q)
    );
  }

  // Keep the selection limited to rows that are currently visible and still exist.
  const visibleKey = filtered.map(f => f.id).join(',');
  useEffect(() => {
    setSelected(prev => {
      if (!prev.size) return prev;
      const visible = new Set(filtered.map(f => f.id));
      const next = new Set(Array.from(prev).filter(id => visible.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [visibleKey]);

  const selectedCount = filtered.filter(f => selected.has(f.id)).length;
  const allSelected   = filtered.length > 0 && selectedCount === filtered.length;

  const toggleSelect = (id, e) => {
    e.stopPropagation();
    setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const toggleSelectAll = () => setSelected(allSelected ? new Set() : new Set(filtered.map(f => f.id)));

  const handleBulkDelete = async () => {
    const ids = filtered.filter(f => selected.has(f.id)).map(f => f.id);
    if (!ids.length || bulkDeleting) return;
    if (!window.confirm(`Delete ${ids.length} firm${ids.length !== 1 ? 's' : ''}? This removes their call history and tasks too. If any were converted to Design Partners, those records stay but lose their link back to the firm. This cannot be undone.`)) return;
    setBulkDeleting(true);
    const results = await Promise.allSettled(ids.map(id => deleteFirm(id)));
    const deleted = new Set(ids.filter((_, i) => results[i].status === 'fulfilled'));
    const failed  = ids.length - deleted.size;
    if (deleted.size) {
      setFirms(prev => prev.filter(f => !deleted.has(f.id)));
      setSelected(prev => new Set(Array.from(prev).filter(id => !deleted.has(id))));
      setEditingCell(prev => (prev && deleted.has(prev.id) ? null : prev));
    }
    toast(failed ? `${deleted.size} deleted, ${failed} failed` : `✓ ${deleted.size} deleted`, failed ? 'error' : 'success');
    setBulkDeleting(false);
  };

  const editInputStyle = {
    width: '100%', background: 'var(--bg-card2)',
    border: '1px solid var(--primary)', borderRadius: 4,
    padding: '4px 6px', fontSize: 12,
    fontFamily: 'DM Sans, sans-serif', outline: 'none',
  };

  const checkTh   = { padding: '8px 10px', textAlign: 'center', fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', borderBottom: '2px solid var(--border)', whiteSpace: 'nowrap', background: 'var(--bg-card)', position: 'sticky', top: 0, left: 0, zIndex: 4, width: 40, minWidth: 40 };
  const checkTd   = { padding: '8px 10px', textAlign: 'center', borderBottom: '1px solid var(--border)', position: 'sticky', left: 0, zIndex: 1, width: 40, minWidth: 40 };
  const frozenTh  = { padding: '8px 10px', textAlign: 'left', fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', borderBottom: '2px solid var(--border)', whiteSpace: 'nowrap', background: 'var(--bg-card)', position: 'sticky', top: 0, left: 40, zIndex: 3, width: 220, minWidth: 220, borderRight: '2px solid var(--border-med)' };
  const normalTh  = { padding: '8px 10px', textAlign: 'left', fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', borderBottom: '2px solid var(--border)', whiteSpace: 'nowrap', background: 'var(--bg-card)', position: 'sticky', top: 0, zIndex: 2 };
  const frozenTd  = { padding: '8px 10px', borderBottom: '1px solid var(--border)', position: 'sticky', left: 40, zIndex: 1, fontWeight: 600, color: 'var(--text)', width: 220, minWidth: 220, maxWidth: 220, borderRight: '2px solid var(--border-med)' };
  const scrollBtn = { background: 'var(--bg-card2)', border: '1px solid var(--border-med)', borderRadius: 6, color: 'var(--text-muted)', width: 28, height: 28, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 600, flexShrink: 0 };

  const inputBoxStyle = {
    background: 'var(--bg-card2)', border: '1px solid var(--border-med)',
    borderRadius: 'var(--radius-sm)', color: 'var(--text)',
    padding: '6px 12px', fontSize: 13,
    fontFamily: 'DM Sans, sans-serif', outline: 'none',
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Loading…</div>;

  // KPI cards always count the full list, never the filtered rows.
  const countStatus = (s) => firms.filter(f => f.status === s).length;
  const INACTIVE_STATUSES = ['Not Interested', 'Do Not Contact', 'Converted → Design Partner'];
  const statusCard = (key, label, status) => ({
    key, label, color: STATUS_COLORS[status], count: countStatus(status),
    active: statusFilter === status,
    onClick: () => setStatusFilter(statusFilter === status ? '' : status),
  });
  const kpiCards = [
    { key: 'total',  label: 'Total',  color: 'var(--primary)', count: firms.length,
      onClick: () => { setStatusFilter(''); setSearch(''); } },
    { key: 'active', label: 'Active', color: 'var(--success)', count: firms.filter(f => !INACTIVE_STATUSES.includes(f.status)).length },
    statusCard('interested', 'Interested',               'Interested'),
    statusCard('demo',       'Demo Scheduled',           'Demo Scheduled'),
    statusCard('candidate',  'Design Partner Candidate', 'Design Partner Candidate'),
    statusCard('converted',  'Converted',                'Converted → Design Partner'),
  ];

  return (
    <>
    <KpiCards cards={kpiCards} columns={6} />
    <div className={`pipeline-view${isPhone ? ' cards' : ''}`} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {/* Quick add */}
      <form className="quick-add" onSubmit={handleQuickAdd} style={{
        display: 'flex', gap: 8, marginBottom: 14,
        background: 'var(--bg-card)', border: '1px solid var(--primary-border)',
        borderRadius: 'var(--radius)', padding: '10px 14px', alignItems: 'center',
      }}>
        <span className="quick-add-label" style={{ fontFamily: 'Sora, sans-serif', fontSize: 12, fontWeight: 600, color: 'var(--primary)', whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          Quick Add · Firm Pipeline
        </span>
        <input
          className="quick-add-input"
          type="text" value={newFirmName}
          onChange={e => setNewFirmName(e.target.value)}
          placeholder="Firm name…"
          style={{ flex: 1, ...inputBoxStyle }}
        />
        <button type="submit" className="quick-add-btn" disabled={!newFirmName.trim() || adding} style={{
          background: newFirmName.trim() && !adding
            ? 'linear-gradient(135deg, var(--primary), var(--primary-dark))'
            : 'var(--bg-card2)',
          border: 'none', borderRadius: 'var(--radius-sm)',
          color: newFirmName.trim() && !adding ? '#fff' : 'var(--text-dim)',
          fontFamily: 'DM Sans, sans-serif', fontWeight: 600, fontSize: 13,
          padding: '6px 20px', cursor: newFirmName.trim() && !adding ? 'pointer' : 'not-allowed',
          whiteSpace: 'nowrap',
        }}>
          {adding ? '…' : '+ Add'}
        </button>
      </form>

      {/* Controls bar */}
      <div className="filters-row single" style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 14, flexShrink: 0, flexWrap: 'wrap' }}>
        <input
          className="filter-search"
          placeholder="Search firms, contacts, emails…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ flex: 1, minWidth: 200, ...inputBoxStyle }}
        />
        <select
          className="filter-select"
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          style={{ ...inputBoxStyle, cursor: 'pointer' }}
        >
          <option value="">All Statuses</option>
          {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        {!isPhone && selectedCount > 0 && (
          <button className="bulk-del" onClick={handleBulkDelete} disabled={bulkDeleting} style={{ background: 'var(--danger-bg)', border: '1px solid rgba(248,113,113,0.3)', borderRadius: 'var(--radius-sm)', color: 'var(--danger)', fontFamily: 'DM Sans, sans-serif', fontSize: 12, fontWeight: 600, padding: '5px 14px', cursor: bulkDeleting ? 'not-allowed' : 'pointer', opacity: bulkDeleting ? 0.6 : 1, whiteSpace: 'nowrap' }}>
            {bulkDeleting ? 'Deleting…' : `🗑 Delete ${selectedCount} selected`}
          </button>
        )}
      </div>

      {isPhone ? (
        /* Phone: read-only card list. Tap a card to open the contact card (all edits happen there). */
        <div>
          {!filtered.length ? (
            <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
              {firms.length ? 'No firms match the current filters.' : 'No firms yet. Use Quick Add to get started.'}
            </div>
          ) : filtered.map(f => {
            const openTasks = f.open_tasks_count || 0;
            return (
              <div
                key={f.id} role="button" tabIndex={0}
                onClick={() => setDetailId(f.id)}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDetailId(f.id); } }}
                style={{
                  background: 'var(--bg-card)', border: '1px solid var(--border)',
                  borderRadius: 'var(--radius)', padding: '12px 14px', marginBottom: 8, cursor: 'pointer',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                  <div style={{ fontWeight: 600, fontSize: 15, color: 'var(--text)', minWidth: 0, wordBreak: 'break-word' }}>{f.firm_name}</div>
                  <span title="Open tasks" style={{
                    flexShrink: 0, padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap',
                    background: openTasks > 0 ? 'var(--warning-bg)' : 'rgba(122,143,170,0.1)',
                    color: openTasks > 0 ? 'var(--warning)' : 'var(--text-dim)',
                  }}>
                    {openTasks} open task{openTasks !== 1 ? 's' : ''}
                  </span>
                </div>
                <div className="phone-card-badges" style={{ marginTop: 6 }}>
                  <StatusBadge status={f.status} />
                </div>
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Next action</div>
                  <div style={{ fontSize: 14, color: f.next_action ? 'var(--text)' : 'var(--text-dim)', wordBreak: 'break-word' }}>{f.next_action || '—'}</div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0 12px', marginTop: 8 }}>
                  <span style={{ fontSize: 14, color: 'var(--text-muted)' }}>{f.contact_name || '—'}</span>
                  {f.phone ? (
                    <a
                      href={telHref(f.phone)} onClick={e => e.stopPropagation()}
                      style={{ display: 'inline-flex', alignItems: 'center', minHeight: 44, color: 'var(--primary)', fontSize: 14, fontWeight: 600, textDecoration: 'none' }}
                    >
                      📞 {f.phone}
                    </a>
                  ) : (
                    <span style={{ fontSize: 14, color: 'var(--text-dim)' }}>—</span>
                  )}
                </div>
              </div>
            );
          })}
          <div style={{ padding: '4px 2px 0', fontSize: 12, color: 'var(--text-dim)' }}>
            {filtered.length} of {firms.length} firm{firms.length !== 1 ? 's' : ''} shown · Tap a card to open the contact card
          </div>
        </div>
      ) : (
      <div style={{ display: 'flex', gap: 8, alignItems: 'stretch', flex: 1, minHeight: 0 }}>
        {/* Scroll controls */}
        <div className="scroll-rail" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '8px 6px', flexShrink: 0 }}>
          <button style={scrollBtn} onClick={() => scrollV(-1)}>▲</button>
          <button style={scrollBtn} onClick={() => scrollV(1)}>▼</button>
          <div style={{ width: 20, height: 1, background: 'var(--border)', margin: '2px 0' }} />
          <button style={scrollBtn} onClick={() => scrollH(-1)}>◀</button>
          <button style={scrollBtn} onClick={() => scrollH(1)}>▶</button>
        </div>

        {/* Table */}
        <div style={{ flex: 1, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          {!filtered.length ? (
            <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13, flex: 1 }}>
              {firms.length ? 'No firms match the current filters.' : 'No firms yet. Use Quick Add to get started.'}
            </div>
          ) : (
            <div ref={scrollRef} style={{ flex: 1, overflowX: 'auto', overflowY: 'auto', position: 'relative', minWidth: 0 }}>
              <table className="touch-table fp-table" style={{ borderCollapse: 'separate', borderSpacing: 0, fontSize: 12 }}>
                <thead>
                  <tr>
                    <th className="rt-check" style={checkTh}>
                      <label className="tap-area">
                        <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} aria-label="Select all firms" style={{ cursor: 'pointer', width: 14, height: 14, accentColor: 'var(--primary)' }} />
                      </label>
                    </th>
                    <th className="fp-frozen" style={frozenTh}>Firm Name</th>
                    <th style={normalTh}>Contact Name</th>
                    <th style={normalTh}>Phone Number</th>
                    <th style={normalTh}>Email</th>
                    <th style={normalTh}>Status</th>
                    <th style={normalTh}>Last Contacted</th>
                    <th style={normalTh}>Next Action</th>
                    <th style={{ ...normalTh, textAlign: 'center' }}>Open Tasks</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((f, idx) => {
                    const isChecked = selected.has(f.id);
                    const baseBg = idx % 2 === 0 ? 'var(--bg-card)' : '#FFFFFF';
                    // Opaque (tint layered over the base colour) so sticky columns never show scrolled content through.
                    const rowBg = isChecked ? `linear-gradient(rgba(46,162,235,0.08), rgba(46,162,235,0.08)), ${baseBg}` : baseBg;
                    const hoverBg = isChecked ? `linear-gradient(rgba(46,162,235,0.12), rgba(46,162,235,0.12)), ${baseBg}` : 'var(--bg-hover)';
                    const statusEditing = editingCell?.id === f.id && editingCell.field === 'status';
                    const actionEditing = editingCell?.id === f.id && editingCell.field === 'next_action';
                    return (
                      <tr key={f.id}
                        style={{ background: rowBg, cursor: 'pointer' }}
                        onClick={() => setDetailId(f.id)}
                        onMouseEnter={e => { if (canHover()) Array.from(e.currentTarget.cells).forEach(cell => { cell.style.background = hoverBg; }); }}
                        onMouseLeave={e => { if (canHover()) Array.from(e.currentTarget.cells).forEach(cell => { cell.style.background = rowBg; }); }}
                      >
                        <td className="rt-check" onClick={e => toggleSelect(f.id, e)}
                          style={{ ...checkTd, background: rowBg, borderLeft: isChecked ? '3px solid var(--primary)' : '3px solid transparent' }}>
                          <input type="checkbox" checked={isChecked} onChange={() => {}} onClick={e => toggleSelect(f.id, e)} aria-label={`Select ${f.firm_name}`} style={{ cursor: 'pointer', width: 14, height: 14, accentColor: 'var(--primary)' }} />
                        </td>
                        <td className="fp-frozen" style={{ ...frozenTd, background: rowBg, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {f.firm_name}
                        </td>
                        <td style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)', background: rowBg, color: 'var(--text-muted)' }}>
                          {f.contact_name || '—'}
                        </td>
                        <td style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)', background: rowBg, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                          {f.phone || '—'}
                        </td>
                        <td style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)', background: rowBg, color: 'var(--text-muted)' }}>
                          {f.email || '—'}
                        </td>

                        {/* Status — inline editable */}
                        <td style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)', background: rowBg }} onClick={e => e.stopPropagation()}>
                          {statusEditing ? (
                            <select
                              autoFocus value={draftValue}
                              onChange={e => { saveField(f.id, 'status', e.target.value); }}
                              onBlur={() => setEditingCell(null)}
                              style={editInputStyle}
                            >
                              {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                          ) : (
                            <div className="edit-cell" onClick={() => startEdit(f.id, 'status', f.status)} style={{ cursor: 'text' }}>
                              <StatusBadge status={f.status} />
                            </div>
                          )}
                        </td>

                        {/* Last Contacted — inline editable */}
                        <td style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)', background: rowBg, whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                          <input
                            type="date"
                            value={f.last_contacted ? String(f.last_contacted).split('T')[0] : ''}
                            onChange={e => saveField(f.id, 'last_contacted', e.target.value || null)}
                            style={editInputStyle}
                          />
                        </td>

                        {/* Next Action — inline editable */}
                        <td style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)', background: rowBg, minWidth: 200 }} onClick={e => e.stopPropagation()}>
                          {actionEditing ? (
                            <input
                              autoFocus type="text" value={draftValue}
                              onChange={e => setDraftValue(e.target.value)}
                              onBlur={() => commitEdit(f.id, 'next_action')}
                              onKeyDown={e => {
                                if (e.key === 'Enter')  { e.preventDefault(); commitEdit(f.id, 'next_action'); }
                                if (e.key === 'Escape') { e.preventDefault(); setEditingCell(null); }
                              }}
                              style={editInputStyle}
                            />
                          ) : (
                            <div className="edit-cell" onClick={() => startEdit(f.id, 'next_action', f.next_action)} style={{ cursor: 'text', color: f.next_action ? 'var(--text)' : 'var(--text-dim)' }}>
                              {f.next_action || '—'}
                            </div>
                          )}
                        </td>

                        {/* Open Tasks — read-only, calculated */}
                        <td style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)', background: rowBg, textAlign: 'center' }}>
                          <span style={{
                            display: 'inline-block', minWidth: 20, padding: '1px 8px', borderRadius: 20,
                            fontSize: 11, fontWeight: 700,
                            background: f.open_tasks_count > 0 ? 'var(--warning-bg)' : 'rgba(122,143,170,0.1)',
                            color: f.open_tasks_count > 0 ? 'var(--warning)' : 'var(--text-dim)',
                          }}>
                            {f.open_tasks_count || 0}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Footer */}
          <div style={{ padding: '7px 14px', fontSize: 11, color: 'var(--text-dim)', borderTop: '1px solid var(--border)', background: 'var(--bg-card)', flexShrink: 0 }}>
            {filtered.length} of {firms.length} firm{firms.length !== 1 ? 's' : ''} shown{selectedCount > 0 && <span style={{ color: 'var(--primary)', marginLeft: 8 }}>· {selectedCount} selected</span>} · Click Status, Last Contacted, or Next Action to edit inline · Click a row to open the firm's contact card
          </div>
        </div>
      </div>
      )}

      {detailId && (
        <FirmContactCard
          firmId={detailId}
          onClose={() => setDetailId(null)}
          onChanged={loadFirms}
          onDeleted={() => { setDetailId(null); loadFirms(); }}
          showToast={toast}
          onOpenDesignPartner={onOpenDesignPartner}
        />
      )}
    </div>
    </>
  );
}
